import * as Sentry from "@sentry/vue";
import { speckleGraphql } from "./speckleGraphql";

/** Where a project member stands with the project's Speckle stream. */
export type SpeckleMemberAccess = "onModel" | "notOnModel" | "noAccount";

/** Whether the reader's own token can open the stream. */
export type OwnSpeckleStreamAccess = "readable" | "refused";

// The code Speckle answers with when the token is valid but the stream is not shared with it.
export const SPECKLE_REFUSED_CODE = "FORBIDDEN";

const EMAILS_PER_REQUEST = 50;

type MemberAccessData = {
  project?: { team?: Array<{ user?: { id?: string } | null }> } | null;
  [alias: `u${number}`]: { items?: Array<{ id?: string }> } | null | undefined;
};

// One document per chunk: the stream's team on the first, and one aliased exact-email search per member.
export const buildMemberAccessQuery = (emails: string[], teamOfStreamId: string | null) => {
  const params = [...(teamOfStreamId ? ["$streamId: String!"] : []), ...emails.map((_, index) => `$e${index}: String!`)];
  const fields = [
    ...(teamOfStreamId ? ["project(id: $streamId) { team { user { id } } }"] : []),
    ...emails.map((_, index) => `u${index}: userSearch(query: $e${index}, limit: 1) { items { id } }`),
  ];
  const variables: Record<string, string> = teamOfStreamId ? { streamId: teamOfStreamId } : {};
  emails.forEach((email, index) => (variables[`e${index}`] = email));
  return { query: `query SpeckleMemberAccess(${params.join(", ")}) { ${fields.join(" ")} }`, variables };
};

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size));

// Each member's standing with the stream, read with the viewer's own token and keyed by lower-cased
// email. Null when any part of the answer is unknown, so a caller flags nobody.
export const getSpeckleMemberAccess = async (
  streamId: string,
  emails: string[]
): Promise<Map<string, SpeckleMemberAccess> | null> => {
  if (!streamId) return null;
  const unique = [...new Set(emails.map((email) => email.trim().toLowerCase()).filter(Boolean))];
  const chunks = chunk(unique, EMAILS_PER_REQUEST);
  if (chunks.length === 0) chunks.push([]);

  try {
    const results = await Promise.all(
      chunks.map((emailsInChunk, index) => {
        const { query, variables } = buildMemberAccessQuery(emailsInChunk, index === 0 ? streamId : null);
        return speckleGraphql<MemberAccessData>(query, variables);
      })
    );
    if (results.some((result) => !result.data)) return null;

    const team = results[0].data?.project?.team;
    if (!team) return null;
    const onStream = new Set(team.map((collaborator) => collaborator.user?.id).filter(Boolean));

    const access = new Map<string, SpeckleMemberAccess>();
    results.forEach((result, chunkIndex) => {
      chunks[chunkIndex].forEach((email, index) => {
        const speckleId = result.data?.[`u${index}`]?.items?.[0]?.id;
        access.set(email, !speckleId ? "noAccount" : onStream.has(speckleId) ? "onModel" : "notOnModel");
      });
    });
    return access;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

const OWN_STREAM_ACCESS_QUERY = `
  query OwnStreamAccess($streamId: String!) {
    project(id: $streamId) {
      id
    }
  }
`;

// Whether the reader's own token can open the stream; a public stream counts as readable. Null for
// anything but a plain refusal (no token, missing stream, server unreachable).
export const getOwnSpeckleStreamAccess = async (streamId: string): Promise<OwnSpeckleStreamAccess | null> => {
  if (!streamId) return null;
  try {
    const { data, codes } = await speckleGraphql<{ project?: { id?: string } | null }>(OWN_STREAM_ACCESS_QUERY, {
      streamId,
    });
    if (data?.project?.id) return "readable";
    if (codes.includes(SPECKLE_REFUSED_CODE)) return "refused";
    return null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};
